import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  SpinnerLoader,
  TotalDueTodayRow,
  Typography,
} from 'shared/ui'
import { numberFormat } from 'shared/lib'
import { PlanHeader } from 'pages/clients/pages/create/ui/ChoosePlan/PlanHeader/PlanHeader'
import { PaymentOptions } from 'pages/clients/pages/create/ui/PaymentOptions/PaymentOptions'
import { CheckoutCardForm } from 'pages/clients/pages/create/ui/Checkout/CheckoutCardForm/CheckoutCardForm'
import { useCreateContext } from 'pages/clients/pages/create/context/createContext'
import { TimePeriodInfo } from 'pages/settings/ui/BillingPlan/ui/TimePeriodInfo/TimePeriodInfo'
import { SecurePaymentInfo } from 'pages/settings/ui/BillingPlan/ui/SecurePaymentInfo/SecurePaymentInfo'
import { LineItem } from 'pages/settings/ui/BillingPlan/ui/LineItem/LineItem'
import styles from './styles.module.scss'

export const Checkout = observer(() => {
  const {
    choosePlanStore,
    previousStep,
    paymentOptions,
    selectedPaymentOption,
    setSelectedPaymentOption,
    isPaymentError,
    handleInviteClient,
    isInviteLoading,
    isPayNow,
    checkoutCardStore,
  } = useCreateContext()
  const {
    period,
    handleSetPeriod,
    handleCheckoutSetPeriod,
    isCheckoutPlanLoading,
    checkoutAnnualSavings,
    checkoutSelectedPlanInfo,
    checkoutPlanLines,
    checkoutTotalPlanData,
    checkoutTotalDueToday,
    startTrialChecked,
    setStartTrialChecked,
    loadPlanData,
  } = choosePlanStore

  useEffect(() => {
    loadPlanData()
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <PlanHeader
          period={period}
          handleSetPeriod={handleSetPeriod}
          previousStep={previousStep}
          isLastStep
          title='Checkout'
        />
      </div>
      {isPaymentError && (
        <Alert
          item={{
            type: 'error',
            title: 'We were unable to process your payment',
            desc: 'Please select a different card',
          }}
          marginBottom={16}
        />
      )}
      <div className={styles.timePeriodInfo}>
        <TimePeriodInfo
          period={period}
          handleSetPeriod={(periodTab) => {
            handleCheckoutSetPeriod({ key: periodTab })
          }}
          isPlanLoading={isCheckoutPlanLoading}
          annualSavings={checkoutAnnualSavings}
          shouldShowPeriodTabs
          selectedPlanInfo={checkoutSelectedPlanInfo}
        />
      </div>
      <div className={styles.totalPlanData}>
        {isCheckoutPlanLoading && (
          <div className={styles.planLoading}>
            <SpinnerLoader ariaLabel={'Checkout'} size={32} />
          </div>
        )}

        <div className={styles.totalPlanDataLines}>
          {checkoutPlanLines.map((item) => (
            <LineItem key={item.title} title={item.title} value={item.value} />
          ))}
        </div>
        <Divider style={{ borderColor: 'var(--black-20)', margin: '12px 0' }} />
        <div className={styles.totalPlanDataLines}>
          {checkoutTotalPlanData.map((item) => (
            <LineItem
              key={item.title}
              title={item.title}
              value={numberFormat({
                value: item.value,
                currency: 'USD',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              color={item.color}
            />
          ))}
        </div>
        <Divider style={{ borderColor: 'var(--black-20)', margin: '12px 0' }} />
        <TotalDueTodayRow totalValue={checkoutTotalDueToday} />
      </div>
      <PaymentOptions
        items={paymentOptions}
        selectedValue={selectedPaymentOption}
        onSelect={setSelectedPaymentOption}
      />
      <div className={styles.dropdownCardsWrapper}>
        {isPayNow && <CheckoutCardForm store={checkoutCardStore} />}
        {period === 'monthly' && (
          <label className={styles.wrapTrialCheckbox} htmlFor='startTrialCheckbox'>
            <Checkbox
              id='startTrialCheckbox'
              checked={startTrialChecked}
              onChange={(value) => setStartTrialChecked(value)}
              style={{ marginRight: 8 }}
              size={16}
            />
            <Typography variant='body-md-regular' ariaLabel='startTrial'>
              {'Start 14-day free trial '}
              <span className={styles.trialText}>(No charge until trial ends)</span>
            </Typography>
          </label>
        )}
      </div>
      <div className={styles.buttonsWrapper}>
        <Button
          fullWidth
          text={'Create client'}
          typeBtn={'contained'}
          contained={'primary'}
          size={'extraLarge'}
          intent={'positive'}
          className={styles.createButton}
          onClick={handleInviteClient}
          disabled={isInviteLoading}
          loading={isInviteLoading}
        />
      </div>
      <SecurePaymentInfo />
    </div>
  )
})
